import { useState } from "react";
import { Check, CircleDot } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";

/** Inline radio-group picker for line items with ambiguous paint-system
 * options. Posts to /api/bid-intake/jobs/:jobId/resolve-ambiguity which:
 *   - Appends "— Selected: X" to the line-item description
 *   - Flips meta.confidence to "high"
 *   - Clears meta.ambiguous_options
 *   - Records a corrections entry on the bid intake job (audit trail)
 *   - Returns the refreshed estimate.
 *
 * Independent per line item — resolving one row does NOT auto-resolve other
 * rows with the same ambiguous_options array.
 */
export default function AmbiguityResolver({
    lineItem,
    index,
    estimateId,
    bidIntakeJobId,
    onResolved,
}) {
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [selected, setSelected] = useState(null);
    const options = lineItem.meta?.ambiguous_options || [];
    if (options.length === 0) return null;

    const resolve = async (choice) => {
        if (!bidIntakeJobId) {
            setErr("Missing bid intake job link — cannot save choice.");
            return;
        }
        setBusy(true);
        setErr("");
        setSelected(choice);
        try {
            const { data } = await api.post(
                `/bid-intake/jobs/${bidIntakeJobId}/resolve-ambiguity`,
                {
                    line_item_index: index,
                    chosen_option: choice,
                    estimate_id: estimateId,
                },
            );
            onResolved?.(data.estimate);
        } catch (e) {
            setErr(apiErrorMessage(e, "Could not save your choice"));
            setSelected(null);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="mt-2 p-3 rounded-md"
            style={{
                background: "rgba(237,108,2,0.06)",
                border: "1px solid rgba(237,108,2,0.25)",
            }}
            data-testid={`ambiguity-resolver-${index}`}
        >
            <p
                className="text-xs font-semibold mb-2"
                style={{ color: "var(--brand-warning)" }}
            >
                Spec lists multiple options — pick one:
            </p>
            <div className="flex flex-col gap-1.5">
                {options.map((opt, i) => {
                    const isSelected = selected === opt;
                    return (
                        <label
                            key={i}
                            className="flex items-start gap-2 cursor-pointer text-sm"
                            data-testid={`ambiguity-option-${index}-${i}`}
                        >
                            <input
                                type="radio"
                                name={`ambig-${index}`}
                                value={opt}
                                disabled={busy}
                                checked={isSelected}
                                onChange={() => resolve(opt)}
                                className="mt-1"
                            />
                            <span>
                                {isSelected ? (
                                    <span className="inline-flex items-center gap-1">
                                        <CircleDot
                                            size={12}
                                            style={{ color: "var(--brand-warning)" }}
                                        />
                                        {opt}
                                    </span>
                                ) : (
                                    opt
                                )}
                            </span>
                        </label>
                    );
                })}
            </div>
            {err && (
                <p
                    className="text-xs mt-2"
                    style={{ color: "var(--brand-red)" }}
                    data-testid={`ambiguity-error-${index}`}
                >
                    {err}
                </p>
            )}
        </div>
    );
}

export function ResolvedPill({ choice }) {
    return (
        <span
            className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ml-2"
            style={{
                background: "rgba(46,125,50,0.12)",
                color: "var(--brand-success)",
                border: "1px solid rgba(46,125,50,0.3)",
            }}
            title={`Resolved: ${choice}`}
            data-testid="ambiguity-resolved-pill"
        >
            <Check size={11} />
            Resolved
        </span>
    );
}
